import React from 'react';
import { Box, Typography, Stack, Avatar, Chip, alpha } from '@mui/material';
import moment from 'moment-timezone';
import {
  AccountBalanceWallet,
  ShoppingCart,
  PhoneAndroid,
  Wifi,
  Tv,
  ElectricBolt,
  AdminPanelSettings,
  RemoveCircle,
  Assignment,
} from '@mui/icons-material';
import { fCurrency } from '../../utils/formatNumber';

const transactionTypeConfig = {
  WALLET_FUNDING: { label: 'Wallet Funding', icon: AccountBalanceWallet, color: '#2563EB' },
  ADMIN_WALLET_FUNDING: { label: 'Admin Funding', icon: AdminPanelSettings, color: '#7C3AED' },
  WALLET_DEDUCTING: { label: 'Wallet Deduct', icon: RemoveCircle, color: '#DC2626' },
  DATA_ORDER: { label: 'Data', icon: Wifi, color: '#0891B2' },
  AIRTIME_ORDER: { label: 'Airtime', icon: PhoneAndroid, color: '#059669' },
  ELECTRICITY_ORDER: { label: 'Electricity', icon: ElectricBolt, color: '#D97706' },
  CABLE_ORDER: { label: 'TV/Cable', icon: Tv, color: '#9333EA' },
  RESULT_CHECK_ORDER: { label: 'Result Check', icon: Assignment, color: '#6366F1' },
};

const statusConfig = {
  success: { color: '#059669', bg: '#ECFDF5', label: 'Success' },
  pending: { color: '#D97706', bg: '#FFFBEB', label: 'Pending' },
  failed: { color: '#DC2626', bg: '#FEF2F2', label: 'Failed' },
  reversed: { color: '#6366F1', bg: '#EEF2FF', label: 'Reversed' },
};

export default function TransactionsCard({ data, onClick }) {
  const date = new Date(data.createdAt);
  const config = transactionTypeConfig[data.transactionType] || {
    label: data.transactionType,
    icon: ShoppingCart,
    color: '#64748B',
  };
  const status = statusConfig[data.status] || statusConfig.pending;
  const IconComponent = config.icon;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        px: 2.5,
        py: 2,
        borderRadius: '14px',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          backgroundColor: alpha(config.color, 0.04),
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 20px ${alpha(config.color, 0.08)}`,
        },
      }}
    >
      {/* Icon Avatar */}
      <Avatar
        sx={{
          width: 48,
          height: 48,
          bgcolor: alpha(config.color, 0.1),
          color: config.color,
        }}
      >
        <IconComponent sx={{ fontSize: 24 }} />
      </Avatar>

      {/* Main Info */}
      <Stack flex={1} spacing={0.3}>
        <Typography
          fontWeight={600}
          fontSize="0.95rem"
          color="text.primary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: { xs: '140px', sm: '280px', md: '400px' },
          }}
        >
          {data.name || data.description || config.label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={config.label}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: alpha(config.color, 0.08),
              color: config.color,
              borderRadius: '6px',
              '& .MuiChip-label': { px: 1 },
            }}
          />
          <Typography fontSize="0.78rem" color="text.secondary">
            {moment(date).tz('Africa/Lagos').format('MMM DD, YYYY')}
          </Typography>
        </Stack>
      </Stack>

      {/* Amount & Status */}
      <Stack alignItems="flex-end" spacing={0.5}>
        <Typography
          fontSize="1rem"
          fontWeight={700}
          color="text.primary"
          sx={{ fontFeatureSettings: '"tnum"' }}
        >
          ₦{fCurrency(data.amount)}
        </Typography>
        <Chip
          label={status.label}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.68rem',
            fontWeight: 700,
            backgroundColor: status.bg,
            color: status.color,
            borderRadius: '6px',
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </Stack>
    </Stack>
  );
}
